import React from "react";
import { Field, useFormState } from "react-final-form";
import Divider from "@material-ui/core/Divider";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import WeduCheckbox from "../../../../../components/WeduCheckbox/WeduCheckbox";
import BootstrapInput from "../../../../../components/BootstrapInput";
import Autocomplete from "../../../../../components/Autocomplete";
import InfoIcon from "../../../../../components/InfoIcon";
import { calcFeeWithScholarshipAndFinancialAid } from "../../../helpers/helperFunctions";
import { ShowCurrency } from "./ShowCurrency";
import { COMMISSION_TYPES } from "../../../../../constants";

const getOtherFeesTotal = (activeTabData) => {
  const total = [
    ...(activeTabData?.mainOtherFees || []),
    ...(activeTabData?.addedOtherFees || []),
  ].reduce((sum, fee) => sum + (+fee?.amount || 0), 0);

  return total < 0 ? 0 : total;
};

const useStyles = makeStyles(() => ({
  mainLabel: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#535358",
    marginTop: 18,
  },
  card: {
    marginTop: 12,
    backgroundColor: "#f2f3f4",
    borderRadius: 12,
    padding: "12px 12px 12px 12px",
  },
  checkboxContainer: {
    display: "flex",
  },
  checkboxTitle: {
    fontSize: 14,
    color: "#a1a4ac",
  },
  checkboxLabel: {
    fontSize: 16,
    color: "#000",
    marginLeft: -3,
    marginTop: 2,
  },
  dropDown: {
    "& .MuiInputBase-root": {
      backgroundColor: "#FFF",
    },
  },
  input: {
    "& input": {
      backgroundColor: "#FFF",
    },
  },
  infoIconCont: {
    lineHeight: 2,
    marginLeft: -14,
  },
  tuitionLabel: {
    fontSize: 18,
    color: "#383d4a",
    fontWeight: "bold",
    marginBottom: 12,
  },
  tuition: {
    fontSize: 18,
    color: "#61657e",
    fontWeight: "bold",
    marginLeft: 18,
  },
  divider: {
    color: "#dae1ed",
    marginTop: 18,
    marginBottom: 24,
  },
  boldStyle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#293858",
  },
  oldTuition: {
    marginLeft: 50,
    textDecoration: "line-through",
  },
  oldOtherFees: {
    marginLeft: 16,
    textDecoration: "line-through",
  },
  calcTuition: {
    marginLeft: 24,
  },
}));

const Calculation = ({ stepName }) => {
  const classes = useStyles();
  const formData = useFormState();

  const activeStepData = formData.values?.[stepName];

  const tuition = +activeStepData?.tuition;
  const currencyId = activeStepData?.currency?.id;

  const hasTuitionScholarship = !!activeStepData?.hasTuitionScholarship;
  const hasTuitionFinancialAid = !!activeStepData?.hasTuitionFinancialAid;
  const hasOtherFeesScholarship = !!activeStepData?.hasOtherFeesScholarship;
  const hasOtherFeesFinancialAid = !!activeStepData?.hasOtherFeesFinancialAid;

  const otherFeesSum = getOtherFeesTotal(activeStepData);

  const {
    feeWithCurrency: tuitionWithCurrency,
    calcFeeWithCurrency: calcTuitionWithCurrency,
    hasSupport: hasTuitionSupport,
  } = calcFeeWithScholarshipAndFinancialAid(
    tuition,
    hasTuitionScholarship,
    activeStepData?.tuitionScholarshipType?.id,
    activeStepData?.tuitionScholarshipAmount,
    hasTuitionFinancialAid,
    activeStepData?.tuitionFinancialAidType?.id,
    activeStepData?.tuitionFinancialAidAmount,
    currencyId,
  );

  const {
    feeWithCurrency: otherFeesWithCurrency,
    calcFeeWithCurrency: calcOtherFeesWithCurrency,
    hasSupport: hasOtherFeesSupport,
  } = calcFeeWithScholarshipAndFinancialAid(
    otherFeesSum,
    hasOtherFeesScholarship,
    activeStepData?.otherFeesScholarshipType?.id,
    activeStepData?.otherFeesScholarshipAmount,
    hasOtherFeesFinancialAid,
    activeStepData?.otherFeesFinancialAidType?.id,
    activeStepData?.otherFeesFinancialAidAmount,
    currencyId,
  );

  return (
    <div>
      <Typography className={classes.mainLabel}>Calculation</Typography>

      <div className={classes.card}>
        <Typography className={classes.tuitionLabel}>
          Tuition:
          <Typography component="span" className={classes.tuition}>
            {tuitionWithCurrency}
          </Typography>
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={3}>
            <Typography className={classes.checkboxTitle}>
              Scholarship
            </Typography>
            <div className={classes.checkboxContainer}>
              <Field
                type="checkbox"
                label={
                  <Typography className={classes.checkboxLabel}>
                    Scholarship Is Available
                  </Typography>
                }
                name={`${stepName}.hasTuitionScholarship`}
                component={WeduCheckbox}
              />
              <div className={classes.infoIconCont}>
                <InfoIcon text="You need to indicate scholarship is given from total amount or just tuition" />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.input}>
            <Field
              fullWidth
              type="text"
              label="Amount"
              placeholder="Amount"
              name={`${stepName}.tuitionScholarshipAmount`}
              component={BootstrapInput}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.dropDown}>
            <Field
              fullWidth
              label="Type"
              options={COMMISSION_TYPES}
              placeholder="Please Select"
              name={`${stepName}.tuitionScholarshipType`}
              component={Autocomplete}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ShowCurrency />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={3}>
            <Typography className={classes.checkboxTitle}>
              Financial Aid
            </Typography>
            <div className={classes.checkboxContainer}>
              <Field
                type="checkbox"
                label={
                  <Typography className={classes.checkboxLabel}>
                    Financial Aid Is Available
                  </Typography>
                }
                name={`${stepName}.hasTuitionFinancialAid`}
                component={WeduCheckbox}
              />
              <div className={classes.infoIconCont}>
                {/*<InfoIcon text='World Education'  />*/}
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.input}>
            <Field
              fullWidth
              type="text"
              label="Amount"
              placeholder="Amount"
              name={`${stepName}.tuitionFinancialAidAmount`}
              component={BootstrapInput}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.dropDown}>
            <Field
              fullWidth
              label="Type"
              options={COMMISSION_TYPES}
              placeholder="Please Select"
              name={`${stepName}.tuitionFinancialAidType`}
              component={Autocomplete}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ShowCurrency />
          </Grid>
        </Grid>
      </div>

      <div className={classes.card}>
        <Typography className={classes.tuitionLabel}>
          Other Fees:
          <Typography component="span" className={classes.tuition}>
            {otherFeesWithCurrency}
          </Typography>
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={3}>
            <Typography className={classes.checkboxTitle}>
              Scholarship
            </Typography>
            <div className={classes.checkboxContainer}>
              <Field
                type="checkbox"
                label={
                  <Typography className={classes.checkboxLabel}>
                    Scholarship Is Available
                  </Typography>
                }
                name={`${stepName}.hasOtherFeesScholarship`}
                component={WeduCheckbox}
              />
              <div className={classes.infoIconCont}>
                <InfoIcon text="You need to indicate scholarship is given from total amount or just tuition" />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.input}>
            <Field
              fullWidth
              type="text"
              label="Amount"
              placeholder="Amount"
              name={`${stepName}.otherFeesScholarshipAmount`}
              component={BootstrapInput}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.dropDown}>
            <Field
              fullWidth
              label="Type"
              options={COMMISSION_TYPES}
              placeholder="Please Select"
              name={`${stepName}.otherFeesScholarshipType`}
              component={Autocomplete}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ShowCurrency />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={3}>
            <Typography className={classes.checkboxTitle}>
              Financial Aid
            </Typography>
            <div className={classes.checkboxContainer}>
              <Field
                type="checkbox"
                label={
                  <Typography className={classes.checkboxLabel}>
                    Financial Aid Is Available
                  </Typography>
                }
                name={`${stepName}.hasOtherFeesFinancialAid`}
                component={WeduCheckbox}
              />
              <div className={classes.infoIconCont}>
                {/*<InfoIcon text='World Education'  />*/}
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.input}>
            <Field
              fullWidth
              type="text"
              label="Amount"
              placeholder="Amount"
              name={`${stepName}.otherFeesFinancialAidAmount`}
              component={BootstrapInput}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.dropDown}>
            <Field
              fullWidth
              label="Type"
              options={COMMISSION_TYPES}
              placeholder="Please Select"
              name={`${stepName}.otherFeesFinancialAidType`}
              component={Autocomplete}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ShowCurrency />
          </Grid>
        </Grid>
      </div>

      <Divider className={classes.divider} />
      <div className={classes.boldStyle}>
        <span>Tuition:</span>
        {hasTuitionSupport && (
          <span className={classes.oldTuition}>{tuitionWithCurrency}</span>
        )}
        <span className={classes.calcTuition}>{calcTuitionWithCurrency}</span>
      </div>
      <div className={classes.boldStyle}>
        <span>Other Fees:</span>
        {hasOtherFeesSupport && (
          <span className={classes.oldOtherFees}>{otherFeesWithCurrency}</span>
        )}
        <span className={classes.calcTuition}>{calcOtherFeesWithCurrency}</span>
      </div>
    </div>
  );
};

export default Calculation;
